import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useSearchParams } from 'react-router-dom'

import BaseMessenger from './BaseMessenger'
import { CLIENT_ACTIVITIES, CLIENT_HOME, CLIENT_NOTES } from '../../../common/constants/routingConstants'
import { getSelectedClientId } from '../../../data/lyraTherapy/clientSelectors'
import { useAppDispatch } from '../../../data/store'
import { setLastConversationClientId } from '../../../data/userSettingsSlice'
import { useLazyGetClientIdQuery } from '../../clients/clientDetails/data/ltClientApi'
import { setSelectedClientId } from '../../clients/clientDetails/data/ltClientSlice'
import { REL_ID_PARAM } from '../hooks/useChatPopout'
import useGetRelId from '../hooks/useGetRelId'
import { useMessageInsertion } from '../hooks/useMessageInsertion'

const PopoutMessenger: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const relId = searchParams.get(REL_ID_PARAM)
  const hasSetInitialClientId = useRef(!relId)
  const dispatch = useAppDispatch()
  const selectedClientId = useSelector(getSelectedClientId)

  const [isComposing, setIsComposing] = useState(!relId)
  const {
    messageToInsert,
    setMessageToInsert,
    currentTemplateId,
    shouldInsertDBL,
    shouldInsertEssentialsLink,
    insertMessage,
    insertDirectBookingLink,
    insertEssentialsArticleChip,
    onDBLInserted,
    onEssentialsLinkInserted,
  } = useMessageInsertion(selectedClientId)

  const [getClientIdFromRelId] = useLazyGetClientIdQuery()
  const getRelIdFromClientId = useGetRelId()
  useEffect(() => {
    const handleRelID = async () => {
      if (relId) {
        const response = await getClientIdFromRelId({ relationshipId: relId }).unwrap()
        if (!response) {
          return
        }
        hasSetInitialClientId.current = true
        dispatch(setSelectedClientId(response.patient_id))
      }
    }

    handleRelID()
  }, [dispatch, getClientIdFromRelId, relId])

  useEffect(() => {
    if (!hasSetInitialClientId.current) {
      return
    }
    if (!selectedClientId) {
      setSearchParams(new URLSearchParams())
      return
    }
    getRelIdFromClientId(selectedClientId).then((patientRelId) => {
      if (patientRelId) {
        setSearchParams(new URLSearchParams({ [REL_ID_PARAM]: patientRelId }))
      }
    })
  }, [getRelIdFromClientId, selectedClientId, setSearchParams])

  const onOpenClientProfile = useCallback(() => {
    if (!relId) {
      return
    }
    const url = new URL(`${window.location.origin}${CLIENT_HOME.route}`)
    url.searchParams.set(REL_ID_PARAM, relId)
    window.open(url, '_blank')
  }, [relId])

  const onOpenAssignment = useCallback(
    (assignmentId: string) => {
      if (!relId) {
        return
      }
      const url = new URL(`${window.location.origin}${CLIENT_ACTIVITIES.route}`)
      url.searchParams.set(REL_ID_PARAM, relId)
      url.searchParams.set('assignmentId', assignmentId)
      window.open(url, '_blank')
    },
    [relId],
  )

  const onOpenNotes = useCallback(() => {
    if (!relId) {
      return
    }
    const url = new URL(`${window.location.origin}${CLIENT_NOTES.route}`)
    url.searchParams.set(REL_ID_PARAM, relId)
    window.open(url, '_blank')
  }, [relId])

  const isLoadingClientId = relId != null && selectedClientId == null
  return (
    <BaseMessenger
      selectedClientId={selectedClientId}
      isComposing={isComposing}
      onSelectedClientIdChanged={(clientId) => {
        dispatch(setSelectedClientId(clientId))
        dispatch(setLastConversationClientId(clientId ?? undefined))
      }}
      showEmptyView={false}
      onIsComposingChanged={setIsComposing}
      onOpenClientProfile={onOpenClientProfile}
      onOpenAssignment={onOpenAssignment}
      onOpenNotes={onOpenNotes}
      initialDetailPanelOpen={false}
      isLoadingClient={isLoadingClientId}
      messageToInsert={messageToInsert}
      insertMessage={insertMessage}
      shouldInsertDBL={shouldInsertDBL}
      onDBLInserted={onDBLInserted}
      insertDirectBookingLink={insertDirectBookingLink}
      shouldInsertEssentialsLink={shouldInsertEssentialsLink}
      onEssentialsLinkInserted={onEssentialsLinkInserted}
      insertEssentialsArticleChip={insertEssentialsArticleChip}
      setMessageToInsert={setMessageToInsert}
      currentTemplateId={currentTemplateId}
    />
  )
}
export default PopoutMessenger
